import { MultiStepFormConfig, BaseFormData } from './components/types';
import { CriteriaWithSelectionStep, StaticContentStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  criteriaWithSelectionStepInitialData,
  combineStepData 
} from './steps/initialData';

// Form options for Buyer Protection
export const formOptions = createFormOptions({
  termLoanCriteria: [
    {
      value: "yes",
      title: "Yes",
      description: "We use <a href='/products/trade-finance' target='_blank'>Trade Finance</a> or <a href='/products/debtor-and-invoice-finance' target='_blank'>Debtor & Invoice Finance</a>."
    },
    {
      value: "no",
      title: "Not yet",
      description: "We\’re not using either"
    }
  ],
});

export const formValidations = createFormValidations({
  // Buyer Protection specific validations
  criteriaSelection: (data: BaseFormData) => !!data.criteriaSelection,
});

// Combine initial data from all steps
const initialFormData = combineStepData(
  criteriaWithSelectionStepInitialData,
);

export const termLoanEnquireFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'term-loan-enquire',
  formOptions: formOptions,
  steps: [
    {
      id: 'criteria-check',
      title: 'Quick suitability check.',
      description: 'Let\’s check if a <strong>Term Loan</strong> could suit your business.',
      component: CriteriaWithSelectionStep,
      validation: formValidations.criteriaSelection,
      selectionQuestion: "Do you currently use any of the following Octet solutions?",
      selectionOptions: formOptions.termLoanCriteria,
      // infoBoxText: "The Octet Term Loan is designed to complement Octet <a href='/products/trade-finance'>Trade Finance</a> or <a href='/products/debtor-and-invoice-finance'>Debtor & Invoice Finance</a>. If you’re not using these solutions yet, you can still complete the form, and we’ll guide you towards the right combination for your business.",
      noSelectionRedirectToThankYou: true,
      noSelectionRedirectToThankValue: "yes",
      showThankYouWithoutSubmit: true,
      noSelectionThankYouConfig: {
        title: 'Thanks for letting us know',
        subtitle: 'As an existing client, your <strong>Relationship Manager</strong> or your dedicated <strong>Sales Manager</strong> is best placed to help you explore a Term Loan tailored to your facility.',
        firstName: '',
        nextStepsTitle: 'Suggested next steps:',
        businessName: '',
        nextStepsItems: [
          {
            icon: 'quick_reference_all',
            text: 'Review your current facility to determine how a Term Loan can complement your existing arrangements.'
          },
          {
            icon: 'contact_support',
            text: 'Contact your Relationship Manager or Sales Manager directly to discuss your eligibility and options or simply call us on <a href="tel:1300862838">1300 862 838</a>.',
          }
        ],
        ctaButton: {
          text: 'View Homepage',
          href: '/'
        },
        additionalInfo: 'In the meantime, stay in-the-know with helpful trends, insights and business guides.',
        socialButton: {
          text: 'Follow Octet on LinkedIn',
          href: 'https://www.linkedin.com/company/octet-finance'
        }
      },
    },
    {
      id: 'term-loan-info',
      title: 'Available with an Octet Finance Facility.',
      description: 'The Octet Term Loan is available when paired with either a <a href="/products/trade-finance" target="_blank" class="text-primary underline">Trade Finance</a> or <a href="/products/debtor-and-invoice-finance" target="_blank" class="text-primary underline">Debtor & Invoice Finance</a> facility. Choose the option that best fits your business needs to continue your enquiry.',
      component: StaticContentStep,
      isOptional: true, // This step doesn't require form submission
      stepConfig: {
        selectionQuestion: 'Which facility would you like to enquire about?',
        selectionOptions: [
          {
            id: 'trade-finance',
            title: 'Octet Trade Finance',
            description: 'Fund local or global supplier payments, bridge cash flow gaps, and access extended repayment terms.',
            redirectUrl: '/enquire/trade-finance',
            isSelected: false
          },
          {
            id: 'debtor-invoice-finance',
            title: 'Octet Debtor & Invoice Finance',
            description: 'Unlock up to 85% of working capital tied up in your unpaid customer invoices. Confidential or disclosed options available.',
            redirectUrl: '/enquire/debtor-invoice-finance',
            isSelected: false
          }
        ],
        ctaButton: {
          text: 'Next',
          variant: 'primary',
          size: 'lg'
        },
      },
    },
  ],
  initialData: initialFormData,
  onSubmit: async () => {},
  hideSubmitButton: true,
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true, // Enable reCAPTCHA v3 protection
  onStepChange: (currentStep, totalSteps) => {
    console.info(`Term Loan form: Step ${currentStep} of ${totalSteps}`);
  },
};
